import React, {useState} from "react";
import {
    Modal,
    ModalOverlay,
    ModalContent,
    ModalHeader,
    ModalBody,
    ModalFooter,
    ModalCloseButton,
    Flex,
    Text, Box, VStack, Button, useToast,
} from "@chakra-ui/react";
import {ScheduledShipmentSimple} from "../../shared/entities/Order/ScheduledShipment";
import {ListProductItemConfig, ListProductItemRow} from "../../components/List/ListProductItem";
import {hasPermission, isRoleAdmin} from "../../features/Permission/AllowedAccess";
import {
    productEditPermission,
    scheduledShipmentCreatePermission,
    scheduledShipmentEditPermission,
    scheduledShipmentForcePermission, scheduledShipmentRemovePermission
} from "../permissions";
import AddScheduledShipmentPopover from "./AddScheduledShipmentPopover";
import IconWithTooltip from "../../components/Icon/IconWithTooltip";
import {FiRepeat, FiTrash} from "react-icons/all";
import {useAppSelector} from "../../redux/Store";
import {Product} from "../../shared/entities/Product/Product";
import ColumnHandler, {ColumnConfig} from "../../components/Buttons/ColumnHandler";
import {FieldType} from "../../common/constants";
import {ModalService} from "../../service/Modal/ModalService";
import {ScheduledShipmentController} from "../../controllers/ScheduledShipmentController";
import {useDispatch} from "react-redux";

interface SubscriptionModalProps {
    product: Product;
    isOpen: boolean;
    onClose: () => void;
}

const SubscriptionModal: React.FC<SubscriptionModalProps> = ({product, isOpen, onClose}) => {
    const adminDealerships = useAppSelector(state => state.UserReducer.dealershipsMinInfo);
    const user = useAppSelector(store => store.AuthReducer.user);
    const dispatch = useDispatch();
    const toast = useToast();

    const modalService = new ModalService(dispatch)
    const scheduledShipmentController = new ScheduledShipmentController(dispatch);

    const [refresh, setRefresh] = useState(false);
    const columnsAll: Array<ColumnConfig> = [
        {
            header: "DEALERSHIP",
            width: "50%",
            type: FieldType.Select,
        },
        {
            header: "QNT",
            width: "12%",
            type: FieldType.Input
        },
        {
            header: "NEXT START DATE",
            isLink: false,
            width: "30%",
            type: FieldType.DateRange
        },
        {
            header: "ACTIONS", filterField: "actions", isLink: false, width: "auto", type: FieldType.Input
        },
    ];
    const dealerships = isRoleAdmin() ? adminDealerships : user?.dealerships?.map(v => ({
        id: v.id,
        name: v.name
    })) || [];
    const confirmRemove = (id: number) => {
        modalService.setModalData({
            onSubmit: () => {
                modalService.deleteModalData();
                scheduledShipmentController.remove({id: id}).then((res) => {
                    toast({
                        position: "top",
                        title: res.None
                            ? `Failed to remove ID: ${id}`
                            : `Success removed. ID: ${id}`,
                        status: res.None ? 'error' : 'success',
                        duration: 3000,
                        isClosable: true,
                    })
                    setRefresh(prevIndex => !prevIndex);
                })
            },
            isOpen: true,
            onOpen: () => {
            },
            onClose: () => {
                modalService.deleteModalData();
            },
            modalTextData: "Are you sure you want to remove item?",
            context: "action"
        });
    };

    const confirmForce = (id: number) => {
        modalService.setModalData({
            onSubmit: () => {
                modalService.deleteModalData();
                scheduledShipmentController.force({id: id}).then((res) => {
                    toast({
                        position: "top",
                        title: res.None
                            ? `Failed to forced ID: ${id}`
                            : `Success forced ID: ${id}`,
                        status: res.None ? 'error' : 'success',
                        duration: 3000,
                        isClosable: true,
                    })
                    setRefresh(prevIndex => !prevIndex);
                })
            },
            isOpen: true,
            onOpen: () => {
            },
            onClose: () => {
                modalService.deleteModalData();
            },
            modalTextData: "Are you sure you want to forced item?",
            context: "action"
        });
    };
    const List = product.shipments ? product.shipments.map((elem: ScheduledShipmentSimple, idx: number) => {
        const ItemConfig: Array<ListProductItemConfig> = [
            {
                content: dealerships.find(v => v.id === elem.dealershipId)?.name || "Unknown",
            },
            {
                content: elem.quantity,
            },
            {
                content: elem.nextStartDate,
            },
            (hasPermission(scheduledShipmentCreatePermission) || hasPermission(scheduledShipmentEditPermission)) && {
                content: <AddScheduledShipmentPopover
                    isSSTable={true}
                    isDisabledSelDealer={true}
                    onSuccess={() => setRefresh(prevIndex => !prevIndex)}
                    item={{...product, quantity: elem.quantity}}
                    editItem={elem}
                    dealer={{
                        id: elem.dealershipId ?? 0,
                        name: dealerships.find(v => v.id === elem.dealershipId)?.name || "Unknown"
                    }}
                />,
                width: "auto"
            },
            // hasPermission(scheduledShipmentForcePermission) && {
            //     content: <IconWithTooltip icon={FiRepeat} tooltipMessage={'force scheduled shipment'}/>,
            //     clickAction: () => confirmForce(elem.id),
            //     isLink: true,
            // },
            hasPermission(scheduledShipmentRemovePermission) && {
                content: <IconWithTooltip icon={FiTrash} tooltipMessage={'remove'}/>,
                clickAction: () => confirmRemove(elem.id),
                isLink: true,
            }
        ].filter(Boolean) as Array<ListProductItemConfig>;
        return <ListProductItemRow key={idx} list={ItemConfig} headers={columnsAll}
                                   style={{fontSize: "13px", minHeight: "35px", justifyContent: "flex-start",}}/>
    }) : null;
    const ColumnNamesMapping: Array<ListProductItemConfig> = columnsAll.map(
        (col: ColumnConfig, index: number) => {
            return {
                content: col.header,
                width: columnsAll[index].width || "auto",
                isLink: col.isLink || false,
                style: col.style || {},
            };
        }
    );

    return (
        <Modal isOpen={isOpen} onClose={onClose} size="lg" isCentered>
            <ModalOverlay/>
            <ModalContent>
                <ModalHeader>
                    <Text fontSize={14} isTruncated fontWeight={"bold"}>{product?.name}</Text>
                </ModalHeader>
                <ModalCloseButton/>
                <ModalBody>
                    <ListProductItemRow list={ColumnNamesMapping} style={{
                        fontSize: "13px",
                        fontWeight: "700",
                        backgroundColor: "aliceblue",
                        justifyContent: "flex-start"
                    }}/>
                    {List}
                    <Flex pt={2}>
                        <Flex ml={"auto"}>
                            <AddScheduledShipmentPopover
                                onSuccess={() => setRefresh(prevIndex => !prevIndex)}
                                item={product}
                            />
                        </Flex>
                    </Flex>
                </ModalBody>
                <ModalFooter/>
            </ModalContent>
        </Modal>
    );
};

export default SubscriptionModal;
